<script setup lang="ts">
import { computed, ref } from 'vue';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { NxtButton, NxtDropdown, NxtIcon, NxtLink } from '@future-harry/nxt-styles';
const viewport = inject<'desktop' | 'tablet' | 'mobile'>('viewport');

const jobBoardStore = useJobBoardStore();

const jobBoard = computed(() => jobBoardStore.currentJobBoard);
const currentLanguage = computed(() => jobBoardStore.activeLanguage);

const logoUrl = computed(() => jobBoard.value?.logo_image_url ?? '');
const companyName = computed(() => jobBoard.value?.name ?? '');
const companyWebsiteUrl = computed(() => jobBoard.value?.home_url ?? '');
const languageOptions = computed(() => jobBoard.value?.languages ?? []);
const currentLanguageId = ref(currentLanguage.value?.id);

const handleLanguageChange = (value: string | number) => {
   const selectedLanguage = jobBoard.value?.languages?.find((lang) => lang.id === value);

   if (selectedLanguage) {
      jobBoardStore.setActiveLanguage(selectedLanguage);

      if (import.meta.env.MODE === 'public') {
         window.location.reload();
      }
   }
};

const openWebsite = () => {
   window.open(companyWebsiteUrl.value, '_blank');
};

function goHome() {
   window.location.replace(window.location.origin + '/');
}
</script>

<template>
   <header :class="['job-board-header', { 'job-board-header--mobile': viewport === 'mobile' }]">
      <NxtLink @click="goHome">
         <div class="flex-center">
            <img v-if="logoUrl" :src="logoUrl" :alt="companyName" class="job-board-header__logo" />
            <h1 class="job-board-header__title">{{ companyName }}</h1>
         </div>
      </NxtLink>
      <div class="flex-center">
         <NxtButton v-if="companyWebsiteUrl" :button-type="'grey'" class="flex-center" @click="openWebsite">
            {{ $tb('components.job-board-header.go-to-website') }}
            <NxtIcon name="arrow-right" size="small" />
         </NxtButton>
         <NxtDropdown
            v-if="jobBoard?.languages && jobBoard.languages.length > 1"
            v-model="currentLanguageId"
            :name="'job-board-language'"
            :options="languageOptions"
            :has-clear-option="false"
            @update:modelValue="handleLanguageChange"
         />
      </div>
   </header>
</template>

<style lang="scss" scoped>
.job-board-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: var(--nxt-gutter-large);
   max-width: 1000px;
   margin: 0 auto;

   :deep(.nxt-dropdown) button {
      line-height: 1;
   }

   h1 {
      color: var(--nxt-dark);
      text-align: left;
   }

   .flex-center {
      display: flex;
      align-items: center;
      gap: var(--nxt-gutter);
   }

   &__logo {
      height: 40px;
      width: auto;
      object-fit: contain;
   }

   @media (max-width: 400px) {
      flex-direction: column;
      align-items: flex-start;
   }

   &.job-board-header--mobile {
      flex-direction: column;
      align-items: flex-start;
   }
}
</style>
